import { useState } from "react";
import styled from "styled-components";
import { toast } from "react-toastify";
import { InputRadio, InputTextarea, ModalForm, Select, Text } from "@ficatranquilo/contabilidade.com-ui";
import useService from "@hooks/useService";
import {
  DECLARATION_FIELDS,
  DECLARATION_TYPES,
  declarationPayload,
  emptyDeclaration,
  validateDeclaration,
} from "@helpers/declarations";
import { focusFirstError } from "../../Common/FormPage";

const Body = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const FIELDS = [DECLARATION_FIELDS.type, DECLARATION_FIELDS.text];

/**
 * Nova declaração do prestador. A assinatura só aparece quando a empresa aceita a
 * anônima; senão, a declaração vai com o nome (o back também barra). Montado só aberto.
 *
 * Props: user (o `user_ow`), allowAnonymous, onClose, onSent.
 */
export default function NewDeclarationModal({ user, allowAnonymous, onClose, onSent }) {
  const declarationsService = useService("declarations");
  const [form, setForm] = useState(emptyDeclaration);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  function close() {
    if (!saving) onClose();
  }

  function set(field, value, id) {
    setForm((prev) => ({ ...prev, [field]: value }));
    if (id && errors[id]) setErrors((prev) => ({ ...prev, [id]: "" }));
  }

  async function send() {
    const found = validateDeclaration(form);
    if (Object.keys(found).length) {
      setErrors(found);
      focusFirstError(FIELDS.filter((id) => found[id]));
      return;
    }

    setSaving(true);
    // Em erro, o service já mostra o toast (com a mensagem do back) e devolve { error: true }
    const result = await declarationsService.create(declarationPayload(form, allowAnonymous));
    setSaving(false);
    if (!result || result.error) return;

    toast.success("Declaração enviada.");
    onSent();
  }

  const signatureOptions = [
    { label: user?.name ? `Com meu nome (${user.name})` : "Com meu nome", value: "named" },
    { label: "Anônima", value: "anonymous" },
  ];

  return (
    <ModalForm
      $showModal
      $setShowModal={close}
      $noClose={saving}
      $isLoading={saving}
      $size="medium"
      $title="Nova declaração"
      $contentBackground="N0"
      $actions={[
        { text: "Cancelar", action: close, variant: "outline", isDisabled: saving },
        { text: "Enviar declaração", action: send, variant: "primary", isDisabled: saving },
      ]}
    >
      <Body>
        <Select
          $idForm={DECLARATION_FIELDS.type}
          $label="Tipo"
          $isRequired
          $errors={errors[DECLARATION_FIELDS.type]}
          $options={DECLARATION_TYPES}
          placeholder="Selecione o tipo"
          value={DECLARATION_TYPES.find((option) => option.value === form.type) || null}
          onChange={(option) => option && set("type", option.value, DECLARATION_FIELDS.type)}
        />
        <InputTextarea
          $idForm={DECLARATION_FIELDS.text}
          $label="Declaração"
          $variant="default"
          $isRequired
          $errors={errors[DECLARATION_FIELDS.text]}
          rows={5}
          placeholder="Escreva sua dúvida, sugestão, reclamação ou elogio"
          value={form.text}
          onChange={(e) => set("text", e.target.value, DECLARATION_FIELDS.text)}
        />
        {allowAnonymous ? (
          <Field>
            <InputRadio
              name={DECLARATION_FIELDS.identify}
              $legend="Assinatura"
              $isColumn
              $options={signatureOptions}
              value={form.identify ? "named" : "anonymous"}
              onChange={(e) => e.target.name === DECLARATION_FIELDS.identify && set("identify", e.target.value === "named")}
            />
            <Text $text="Na anônima, ninguém da empresa vê quem enviou." $variant="caption_medium" $color="N400" />
          </Field>
        ) : null}
      </Body>
    </ModalForm>
  );
}
